/**
 * Iter C.2.1.CAP — Capability badges/icons (Presentation, soft taxonomy).
 *
 * Pure display component. Takes a list of capability codes (free-text,
 * normalized server-side to lower-snake-case) and renders a compact
 * chip strip with a Phosphor icon + label per chip. Unknown codes fall
 * back to the generic Tag icon and the code itself as the label.
 *
 * Per Rahul's 2026-02 direction: lightweight visual communication only;
 * NO workflow or governance complexity layered in.
 *
 * Sourced suggestions list (icon + label per code) is fetched once and
 * cached in module state to avoid repeated lookups across mount points.
 *
 * Usage:
 *   <CapabilityBadges capabilities={item.capabilities} size="sm" />
 */
import React, { useEffect, useState } from "react";
import {
    Globe, AndroidLogo, AppleLogo, Plug, ChartBar, TrendUp,
    Buildings, ChartLineUp, PaintBrush, Tag,
} from "@phosphor-icons/react";
import { api } from "../../lib/api";

const PHOSPHOR_REGISTRY = {
    Globe, AndroidLogo, AppleLogo, Plug, ChartBar, TrendUp,
    Buildings, ChartLineUp, PaintBrush, Tag,
};

// Module-level cache so the suggestions list is fetched once across all
// mount points. Capability metadata is global + low-churn.
let _suggestionsCache = null;
let _suggestionsInflight = null;

async function getSuggestionsMap() {
    if (_suggestionsCache) return _suggestionsCache;
    if (_suggestionsInflight) return _suggestionsInflight;
    _suggestionsInflight = (async () => {
        try {
            const r = await api.get("/billing/catalogue/capability-suggestions");
            _suggestionsCache = (r.data.items || []).reduce((m, it) => {
                m[it.code] = it;
                return m;
            }, {});
            return _suggestionsCache;
        } catch (_e) {
            _suggestionsCache = {};
            return _suggestionsCache;
        } finally {
            _suggestionsInflight = null;
        }
    })();
    return _suggestionsInflight;
}

/** Convert a raw code like "white_label" into a human-readable fallback
 * label ("White Label") when the code is unknown to the suggestions list. */
const humanise = (code) =>
    String(code || "").split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");

export function CapabilityBadges({ capabilities, size = "sm", className = "" }) {
    const [map, setMap] = useState(_suggestionsCache || {});

    useEffect(() => {
        let alive = true;
        if (!_suggestionsCache) {
            getSuggestionsMap().then((m) => { if (alive) setMap(m); });
        }
        return () => { alive = false; };
    }, []);

    const list = Array.isArray(capabilities) ? capabilities : [];
    if (list.length === 0) return null;

    const px = size === "lg" ? 14 : size === "md" ? 12 : 11;
    const textCls = size === "lg" ? "text-xs" : "text-[10px]";

    return (
        <div
            className={`flex flex-wrap items-center gap-1 ${className}`}
            data-testid="capability-badges"
        >
            {list.map((code) => {
                const meta = map[code];
                const Icon = (meta && PHOSPHOR_REGISTRY[meta.icon]) || Tag;
                const label = meta?.label || humanise(code);
                return (
                    <span
                        key={code}
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200 ${textCls}`}
                        title={label}
                        data-testid={`capability-badge-${code}`}
                    >
                        <Icon size={px} weight="bold" />
                        <span className="font-medium">{label}</span>
                    </span>
                );
            })}
        </div>
    );
}

export default CapabilityBadges;
